import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/layout/Navbar';
import AdminLayout from './components/layout/AdminLayout';
import ThemeSync from './components/layout/ThemeSync';
import HomePage from './pages/public/HomePage';
import LoginPage from './pages/public/LoginPage';
import OemPage from './pages/public/OemPage';
import BrandPage from './pages/public/BrandPage';
import ContactPage from './pages/public/ContactPage';
import CatalogPage from './pages/public/CatalogPage';
import ProductDetailPage from './pages/public/ProductDetailPage';
import Dashboard from './pages/admin/Dashboard';
import InventoryPage from './pages/admin/InventoryPage';
import ProductionPage from './pages/admin/ProductionPage';
import UsersPage from './pages/admin/UsersPage';
import ProfilePage from './pages/admin/ProfilePage';
import VendorsPage from './pages/admin/VendorsPage';
import ProductCatalogPage from './pages/admin/ProductCatalogPage';
import ProductDetailEditor from './pages/admin/ProductDetailEditor';
import ReportPage from './pages/admin/ReportPage';
import InquiryPage from './pages/admin/InquiryPage';
import SettingsPage from './pages/admin/SettingsPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-stone-900 flex items-center justify-center">
      <div className="text-amber-400 text-sm tracking-widest animate-pulse">COBRAND ...</div>
    </div>
  );
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function PublicLayout({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Navbar />
      {children}
    </>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/brand"   element={<PublicLayout><BrandPage /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
      <Route path="/oem"     element={<PublicLayout><OemPage /></PublicLayout>} />
      <Route path="/catalog"      element={<PublicLayout><CatalogPage /></PublicLayout>} />
      <Route path="/product/:id"  element={<PublicLayout><ProductDetailPage /></PublicLayout>} />

      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="production" element={<ProductionPage />} />
        <Route path="inquiries" element={<InquiryPage />} />
        <Route path="report"    element={<ReportPage />} />
        <Route path="users"   element={<UsersPage />} />
        <Route path="catalog"           element={<ProductCatalogPage />} />
        <Route path="catalog/:id/edit" element={<ProductDetailEditor />} />
        <Route path="vendors" element={<VendorsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <ThemeSync />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}
